// src/pages/staff/lessonplan/lessonplanlist/lessonPlanApprovalColumns.js
import React, { useEffect, useState } from "react";
import moment from "moment";
import dayjs from "dayjs";
import styles from "../../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import CustomDatePicker from "../../../../../components/CustomDatePicker/CustomDatePicker";
import dropdownStyles from "./ViewAllLessPlanApproval/ViewLessonPlanApproval.module.css";

const renderCellWithDash = ({ cell }) => {
  const value = cell.getValue();
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

export const getStatusBadge = (statusText) => {
  if (!statusText) return "-";
  const key = String(statusText).toLowerCase().replace(/[-\s]/g, "");

  const classMap = {
    submitted: styles.statusSubmitted,
    approved: styles.statusApproved,
    rejected: styles.statusRejected,
    resubmitted: styles.statusResubmitted,
    completed: styles.statusCompleted,
  };

  return (
    <span className={`${styles.statusBadge} ${classMap[key] || ""}`}>
      {statusText}
    </span>
  );
};

/**
 * Options shown in the column HEADER filter dropdown.
 * Includes all filterable states (Completed excluded — terminal).
 */
const STATUS_FILTER_OPTIONS = [
  { text: "Submitted", value: "Submitted" },
  { text: "Approved", value: "Approved" },
  { text: "Rejected", value: "Rejected" },
  { text: "Completed", value: "Completed" },
];

/**
 * HM action options shown in the row-level Status dropdown.
 */
const HM_ACTION_OPTIONS = [
  { text: "Approved", value: "Approved" },
  { text: "Rejected", value: "Rejected" },
];

/**
 * Date range filter using the reusable CustomDatePicker.
 */
const DateRangeFilter = ({ column, translations, onDateRangePicked }) => {
  const filterValue = column.getFilterValue();
  const extStart =
    Array.isArray(filterValue) && filterValue[0] ? filterValue[0] : "";
  const extEnd =
    Array.isArray(filterValue) && filterValue[1] ? filterValue[1] : "";

  const [localStart, setLocalStart] = useState(extStart);
  const [localEnd, setLocalEnd] = useState(extEnd);

  useEffect(() => {
    setLocalStart(extStart);
    setLocalEnd(extEnd);
  }, [extStart, extEnd]);

  const startValue = localStart ? dayjs(localStart) : null;
  const endValue = localEnd ? dayjs(localEnd) : null;

  const commitFilter = (nextStart, nextEnd) => {
    if (!nextStart && !nextEnd) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue([nextStart || "", nextEnd || ""]);
    }
  };

  const handleStartChange = (dayjsDate) => {
    const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
    setLocalStart(formatted);
    commitFilter(formatted, localEnd);
    if (formatted && typeof onDateRangePicked === "function") {
      onDateRangePicked();
    }
  };

  const handleEndChange = (dayjsDate) => {
    const formatted = dayjsDate ? dayjs(dayjsDate).format("YYYY-MM-DD") : "";
    setLocalEnd(formatted);
    commitFilter(localStart, formatted);
    if (formatted && typeof onDateRangePicked === "function") {
      onDateRangePicked();
    }
  };

  return (
    <div className={styles.dateRangeFilter}>
      <CustomDatePicker
        name={`${column.id}_start`}
        value={startValue}
        onChange={handleStartChange}
        placeholder={
          translations?.labels?.startDatePlaceholder || "Start Date"
        }
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
        maxDate={endValue || undefined}
        className={styles.dateRangePicker}
      />
      <CustomDatePicker
        name={`${column.id}_end`}
        value={endValue}
        onChange={handleEndChange}
        placeholder={translations?.labels?.endDatePlaceholder || "End Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
        minDate={startValue || undefined}
        className={styles.dateRangePicker}
      />
    </div>
  );
};

/**
 * HM Status action dropdown.
 * - Pre-selects the row's current status (e.g. "Submitted").
 * - Offers Approve / Reject as the actions the HM can take.
 * - For terminal "Completed" status, shows only the badge.
 *
 * NOTE on revert behavior:
 * The parent (`AllLessonPlanApprovalList`) gives this component a
 * `key` derived from `revertSignals[rowId]`. When the user picks
 * "Rejected" and then clicks Cancel in the modal, the parent bumps
 * that signal. React then unmounts this component and mounts a fresh
 * one — its initial `useState(currentStatus)` re-reads the row's
 * actual status from `row.original.status_text`, so the visible
 * `<select>` value snaps back to the previous status (e.g. "Submitted").
 */
const StatusActionDropdown = ({ row, onStatusChange, translations }) => {
  const currentStatus = row?.original?.status_text || "";
  const currentStatusLower = String(currentStatus).toLowerCase();

  const isCompleted = currentStatusLower === "completed";
  const isLockedStatus =
    currentStatusLower === "approved" || currentStatusLower === "rejected";

  const [value, setValue] = useState(currentStatus);
  useEffect(() => {
    setValue(currentStatus);
  }, [currentStatus]);

  // Optimistic lock: the moment the HM picks Approve/Reject we want to
  // disable the dropdown immediately so they can't double-click.
  // BUT — if the action is Reject (which opens a confirmation modal),
  // we must NOT lock, because the user might Cancel and we then need
  // the dropdown to be re-usable after the revert remount.
  const [justActed, setJustActed] = useState(false);
  useEffect(() => {
    if (isLockedStatus) setJustActed(false);
  }, [isLockedStatus]);

  const handleChange = (e) => {
    const newStatus = e.target.value;
    if (!newStatus || newStatus === currentStatus) return;

    setValue(newStatus);

    // Only optimistically lock for Approve (immediate action).
    // For Reject we wait for the modal outcome — Cancel will remount
    // this component fresh, Confirm will refetch with new status.
    if (String(newStatus).toLowerCase() === "approved") {
      setJustActed(true);
    }

    if (typeof onStatusChange === "function") {
      onStatusChange(row.original, newStatus);
    }
  };

  if (isCompleted) {
    return getStatusBadge(currentStatus);
  }

  const disabled = isLockedStatus || justActed;

  const currentOption = currentStatus
    ? [{ text: currentStatus, value: currentStatus }]
    : [];

  const actionOptions = HM_ACTION_OPTIONS.filter(
    (opt) => opt.value !== currentStatus
  );

  const options = [...currentOption, ...actionOptions];

  return (
    <select
      className={dropdownStyles.statusDropdown}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.text}
        </option>
      ))}
    </select>
  );
};

/**
 * Build columns array based on tab needs.
 *
 * @param opts.onStatusChange   callback(rowData, newStatus)
 * @param opts.revertSignals    map<rowId, number> — bump a row's number
 *                              to force its Status dropdown to re-mount
 *                              and snap back to row.original.status_text
 */
export const buildLessonPlanApprovalColumns = (opts) => {
  const {
    translations = {},
    topicOptions = [],
    subTopicOptions = [],
    includeScheduledDate = false,
    onStatusChange = null,
    onDateRangePicked,
    revertSignals = {},
  } = opts;

  const cols = [
    {
      accessorKey: "staff_name",
      header: translations?.labels?.teachersName || "Teachers Name",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.staff_name || "Teacher Name",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "book",
      header: translations?.labels?.subject || "Subject",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.subject || "Subject",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "periods",
      header: translations?.labels?.perios || "Period",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.perios || "Period",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "dayid",
      header: translations?.labels?.day || "Day",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.day || "Day",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "class_code",
      header: translations?.labels?.class || "Class",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.class || "Class",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "section",
      header: translations?.labels?.section || "Section",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.section || "Section",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "medium",
      header: translations?.labels?.medium || "Medium",
      enableSorting: true,
      enableColumnFilter: true,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.medium || "Medium",
      },
      Cell: renderCellWithDash,
    },
    {
      accessorKey: "scheduled_date",
      header: translations?.labels?.scheduledDate || "Scheduled Date",
      enableSorting: true,
      enableColumnFilter: true,
      Filter: ({ column }) => (
        <DateRangeFilter
          column={column}
          translations={translations}
          onDateRangePicked={onDateRangePicked}
        />
      ),
      Cell: ({ cell }) => {
        const v = cell.getValue();
        if (!v) return "-";
        return moment(v).format("DD/MM/YYYY");
      },
      size: 280,
    },
    {
      accessorKey: "status_text",
      header: translations?.labels?.status || "Status",
      enableSorting: true,
      enableColumnFilter: true,
      filterVariant: "select",
      filterSelectOptions: STATUS_FILTER_OPTIONS,
      muiFilterTextFieldProps: {
        placeholder: translations?.labels?.status || "Status",
      },
      Cell: ({ cell, row }) => {
        if (onStatusChange) {
          // Use the per-row revert signal as part of the key so this
          // dropdown re-mounts (and re-reads its initial state from
          // row.original.status_text) when the parent bumps the signal
          // — i.e. when the user clicks Cancel in the rejection modal.
          const rowId = row?.original?.id;
          const signal = revertSignals[rowId] || 0;
          const cellKey = `status-${rowId}-${signal}`;

          return (
            <StatusActionDropdown
              key={cellKey}
              row={row}
              onStatusChange={onStatusChange}
              translations={translations}
            />
          );
        }
        return getStatusBadge(cell.getValue());
      },
      size: 200,
    },
  ];

  return cols;
};